/**
 * @fileoverview
 *
 * WidgetAppContext is a HOC which makes sure the Widget component
 * get appContext resolved and stored in the store.
 */

import { lazy, Suspense, useEffect, useRef, useSyncExternalStore } from 'react'

import { allTests, getGuestBucket } from '@core/abTesting'
import {
  isBackendABTestingInitialized,
  isInEmbedVisibilityHideExperimentVariant,
  isInEmbedVisibilityShowExperimentVariant,
  isInFireworkVisibilityHideExperimentVariant,
  isInFireworkVisibilityShowExperimentVariant,
  subscribeBackendABTestingInitialized,
} from '@core/abTesting/backend/backendABTesting'
import { ABTestID } from '@core/abTesting/types'
import { useFeedQueryContext } from '@core/feed'
import { getGlobalConfig } from '@core/globalConfig'
import { persistFWReferrer } from '@core/tracking'
import { usePersistChannelId } from '@core/tracking/funnelTracking/hooks'
import { useStore } from '@core/widgets/store'
import { paramsSelector } from '@core/widgets/store/selectors'
import { useTrackControlGroupEmbedInstance } from '@core/widgets/tracking/useTrackControlGroupEmbedInstance'
import { useTrackEmbedInstance } from '@core/widgets/tracking/useTrackEmbedInstance'
import type { AppContextType } from '@embed/components/helpers'
import { useEmbedAppContext } from '@embed/hooks/useEmbedAppContext'
import { isShopifyEditor } from '@embed/tools/shopify'

const PlgIntersectionObserver = lazy(
  () => import('@embed/components/common/PlgIntersectionObserver'),
)

interface IProps {
  appContextType: AppContextType
}

/**
 * HOC that builds the app context and keeps it updated in the widget store.
 */
export const WidgetAppContext: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = (props) => {
  const { children, appContextType } = props

  const wasEmbedInstanceTracked = useRef(false)

  const actions = useStore((state) => state.actions)
  const params = useStore(paramsSelector)
  const [{ config }] = useFeedQueryContext()

  const appContext = useEmbedAppContext({
    config,
    appContextType,
  })

  const areExperimentsReady = useSyncExternalStore(
    subscribeBackendABTestingInitialized,
    isBackendABTestingInitialized,
    () => false,
  )

  const trackEmbedInstance = useTrackEmbedInstance()
  const trackControlGroupEmbedInstance = useTrackControlGroupEmbedInstance()

  // Data team wants the last channel ID to be set at this level and not within player.
  // This is because with things like 'embed-feed' a user can watch multiple videos
  // and never click into the player, so the channel is persisted here.
  usePersistChannelId(appContext?.channel?.encoded_id)

  useEffect(() => {
    // Important to update fwreferrer from params.page_url in case widget is
    // being used in an iframe and access to window.location is limited.
    persistFWReferrer(params.page_url ?? '')
  }, [params.page_url])

  const plgTest = allTests.get(ABTestID.PLG)
  const guestBucket = getGuestBucket(plgTest)

  const fivePercentTest = allTests.get(
    ABTestID.NINETY_FIVE_PERCENT_TRAFFIC_WIDGET_DISPLAY,
  )
  const fivePercentBucket = getGuestBucket(fivePercentTest)

  const isInInvisibleVariant = isInEmbedVisibilityHideExperimentVariant()
  const isInVisibleVariant = isInEmbedVisibilityShowExperimentVariant()
  const isInFireworkVisibilityHideVariant =
    isInFireworkVisibilityHideExperimentVariant()
  const isInFireworkVisibilityShowVariant =
    isInFireworkVisibilityShowExperimentVariant()

  const isHide =
    guestBucket?.id === 'invisible' ||
    fivePercentBucket?.id === 'invisible' ||
    isInInvisibleVariant ||
    isInFireworkVisibilityHideVariant
  const isShow =
    (!guestBucket || guestBucket.id === 'visible') &&
    (!fivePercentBucket || fivePercentBucket.id === 'visible') &&
    (!isInInvisibleVariant || isInVisibleVariant) &&
    (!isInFireworkVisibilityHideVariant || isInFireworkVisibilityShowVariant)
  const businessId =
    params.business_id ||
    appContext?.channel?.business_id ||
    getGlobalConfig().business_id

  useEffect(() => {
    if (!appContext || wasEmbedInstanceTracked.current) {
      return
    }

    if (isHide) {
      trackControlGroupEmbedInstance(appContext)
      wasEmbedInstanceTracked.current = true
      return
    }

    if (!isShow) {
      return
    }

    // Wait for backend A/B resolution when init will run so show/hide
    // instance counts measure the same post-resolution population.
    if (!areExperimentsReady && businessId) {
      return
    }

    trackEmbedInstance(appContext)
    wasEmbedInstanceTracked.current = true
  }, [
    appContext,
    areExperimentsReady,
    businessId,
    isHide,
    isShow,
    trackControlGroupEmbedInstance,
    trackEmbedInstance,
  ])

  useEffect(() => {
    if (appContext) {
      actions.setContext(appContext)
    }
  }, [appContext, actions])

  return (guestBucket?.id === 'invisible' ||
    fivePercentBucket?.id === 'invisible' ||
    isInInvisibleVariant ||
    isInFireworkVisibilityHideVariant) &&
    !isShopifyEditor() ? (
    <Suspense fallback={null}>
      <PlgIntersectionObserver />
    </Suspense>
  ) : (
    children
  )
}
